/**
 * MEIA Studio — Scoring Engine Type Definitions
 * Schema Version: v4.3.5 | Algorithm Version: 1.2.0
 *
 * These types define the contract between the schema configuration and
 * the application scoring engine. They are the single source of truth
 * for MEIA enum values used across both shared and backend layers.
 *
 * GOVERNANCE: Changes to these types must ship with matching schema
 * and DB migration updates in the same PR.
 *
 * DOCTRINE ALIGNMENT: Types enforce deterministic behavior — all enums
 * are exhaustive, all nullable fields are explicit, and all scoring
 * outputs carry provenance metadata for HITL audit compliance.
 *
 * All enums use SCREAMING_SNAKE values to match the Azymmetric Postgres-enum
 * convention. `ScoringBand` is unified as a single enum including PENDING;
 * service-layer code enforces PENDING-forbidden rules on override columns.
 */
// ─────────────────────────────────────────────────────────────
// Band & Scoring Primitives
// ─────────────────────────────────────────────────────────────
/**
 * Three-color scoring band per DoDI 5000.MEI Steps 8-9, unified with
 * PENDING for unscored/missing data states.
 *
 * Service layer enforces `band !== PENDING` on override columns
 * (e.g. `StudyOverride.overrideBand`, `BandTransition.fromBand/toBand`,
 * `StableRange.band`, `ContestResult.band`).
 */
export const ScoringBand = {
    GREEN: 'GREEN',
    PENDING: 'PENDING',
    RED: 'RED',
    YELLOW: 'YELLOW',
};
export const StudyScoringAggregationMethod = {
    GATED_MULTIPLY: 'GATED_MULTIPLY',
    WEAKEST_LINK: 'WEAKEST_LINK',
    WEIGHTED_AVERAGE: 'WEIGHTED_AVERAGE',
};
export const DEFAULT_STUDY_SCORING_CONFIG = {
    aggregationMethod: StudyScoringAggregationMethod.WEAKEST_LINK,
    greenThreshold: 80,
    yellowThreshold: 60,
};
/**
 * Band numeric mapping — the mathematical representation of bands.
 * GREEN=1.0, YELLOW=0.5, RED=0.0. PENDING has no numeric value.
 *
 * CRITICAL: These values are contractual. Changing them alters every
 * computed score in the system and requires an algorithmVersion bump.
 */
export const BAND_NUMERIC = {
    [ScoringBand.GREEN]: 1.0,
    [ScoringBand.RED]: 0.0,
    [ScoringBand.YELLOW]: 0.5,
};
/** Convert numeric score back to band. */
export function bandFromScore(score, scoringConfig) {
    const greenThreshold = scoringConfig ? scoringConfig.greenThreshold / 100 : 0.9;
    const yellowThreshold = scoringConfig ? scoringConfig.yellowThreshold / 100 : 0.5;
    if (score >= greenThreshold)
        return ScoringBand.GREEN;
    if (score >= yellowThreshold)
        return ScoringBand.YELLOW;
    return ScoringBand.RED;
}
/** Convert band to numeric, PENDING -> null. */
export function bandNumeric(band) {
    if (band === ScoringBand.PENDING)
        return null;
    return BAND_NUMERIC[band];
}
/** Compare bands by severity. Returns negative when left is worse. */
export function compareBands(left, right) {
    return BAND_NUMERIC[left] - BAND_NUMERIC[right];
}
/** Return worse band. */
export function worseBand(left, right) {
    return compareBands(left, right) <= 0 ? left : right;
}
/** Return better band. */
export function betterBand(left, right) {
    return compareBands(left, right) >= 0 ? left : right;
}
// ─────────────────────────────────────────────────────────────
// Dashboard Score Normalization
// ─────────────────────────────────────────────────────────────
//
// Both the API (StudyScoringOutput hierarchy) and the dashboard UI need
// the same band-to-score mapping and the same 0-100 normalization. Keep
// these helpers here so the FE and BE never drift.
export const DASHBOARD_SCORE_MAX = 100;
export const DASHBOARD_SCORE_MIN = 0;
/**
 * Normalize a raw score (either 0-1 fractional or 0-100 percent) to a
 * 0-100 integer. Returns null for null/undefined/non-finite inputs.
 */
export function normalizeDashboardScore(score) {
    if (score === null || score === undefined || !Number.isFinite(score))
        return null;
    const scaledScore = score <= 1 ? score * DASHBOARD_SCORE_MAX : score;
    return Math.round(Math.min(Math.max(scaledScore, DASHBOARD_SCORE_MIN), DASHBOARD_SCORE_MAX));
}
/** Clamp an arbitrary number to the 0-100 dashboard range. */
export function clampDashboardPercentage(value) {
    return Math.min(Math.max(value, DASHBOARD_SCORE_MIN), DASHBOARD_SCORE_MAX);
}
/** Map a band to its dashboard score (0-100). PENDING/nullish → null. */
export function dashboardScoreFromBand(band) {
    if (band === null || band === undefined || band === ScoringBand.PENDING)
        return null;
    return Math.round((BAND_NUMERIC[band] ?? 0) * DASHBOARD_SCORE_MAX);
}
/**
 * Average a list of finite numeric scores. Returns null for an empty
 * list so callers can distinguish "no data" from "average is zero".
 */
export function averageScores(scores) {
    if (scores.length === 0)
        return null;
    return scores.reduce((sum, score) => sum + score, 0) / scores.length;
}
// ─────────────────────────────────────────────────────────────
// Scoring Configuration Enums
// ─────────────────────────────────────────────────────────────
/** How a task contributes to its parent MOE */
export const ScoringRole = {
    BLOCKING: 'BLOCKING',
    CONTRIBUTING: 'CONTRIBUTING',
};
/**
 * Cross-technology gate condition for blocking tasks.
 * ANY_RED: single point of failure — one RED tech fails the task.
 * ALL_RED: redundant systems — all techs must be RED to fail.
 */
export const BlockingCondition = {
    ALL_RED: 'ALL_RED',
    ANY_RED: 'ANY_RED',
};
/**
 * How multiple MOPs aggregate within a single technology for a task.
 * WORST: AND-logic — all MOPs must pass (default).
 * BEST:  OR-logic — any MOP passing suffices (rare).
 * AVERAGE: partial credit across MOP dimensions.
 */
export const MopAggregation = {
    AVERAGE: 'AVERAGE',
    BEST: 'BEST',
    WORST: 'WORST',
};
/**
 * MOE aggregation method — drives Step 2 logic path selection.
 * WORST_CASE_BLOCKING: pure blocking gate (no contributing tasks).
 * WEIGHTED_CONTRIBUTING: pure weighted average (no blocking tasks).
 * HYBRID: blocking gate first, then weighted contributing if gate passes.
 */
export const ScoringAggregationMethod = {
    HYBRID: 'HYBRID',
    WEIGHTED_CONTRIBUTING: 'WEIGHTED_CONTRIBUTING',
    WORST_CASE_BLOCKING: 'WORST_CASE_BLOCKING',
};
/**
 * How YELLOW blocking tasks affect the parent MOE.
 * PROPAGATE_YELLOW: YELLOW blocking task sets a YELLOW ceiling on MOE.
 * IGNORE: YELLOW blocking tasks treated as GREEN for the gate.
 */
export const BlockingYellowBehavior = {
    IGNORE: 'IGNORE',
    PROPAGATE_YELLOW: 'PROPAGATE_YELLOW',
};
/**
 * How PENDING (null) MOP values propagate through scoring.
 * TREAT_AS_RED: pessimistic — missing data = failure.
 * TREAT_AS_YELLOW: neutral — missing data = partial.
 * EXCLUDE_FROM_AVERAGE: skip nulls, flag partiallyScored. Recommended default.
 * BLOCK_SCORING: halt — no incomplete data allowed. Use for final-stage scoring.
 */
export const PendingMopBehavior = {
    BLOCK_SCORING: 'BLOCK_SCORING',
    EXCLUDE_FROM_AVERAGE: 'EXCLUDE_FROM_AVERAGE',
    TREAT_AS_RED: 'TREAT_AS_RED',
    TREAT_AS_YELLOW: 'TREAT_AS_YELLOW',
};
/**
 * How YELLOW MOE/MOS bands are dispositioned.
 * ACCEPT: YELLOW counts as pass.
 * ACCEPT_WITH_OVERRIDE: YELLOW is conditional — requires analyst override.
 * REJECT: YELLOW reclassified as RED.
 */
export const YellowDisposition = {
    ACCEPT: 'ACCEPT',
    ACCEPT_WITH_OVERRIDE: 'ACCEPT_WITH_OVERRIDE',
    REJECT: 'REJECT',
};
/** Minimum band required for pass */
export const PassBand = {
    GREEN: 'GREEN',
    YELLOW_OR_ABOVE: 'YELLOW_OR_ABOVE',
};
/** MOP category — routes to correct scoring pathway */
export const MopCategory = {
    PROCESS_RELIABILITY: 'PROCESS_RELIABILITY',
    TECHNICAL_PHYSICAL: 'TECHNICAL_PHYSICAL',
    TIME_LATENCY: 'TIME_LATENCY',
};
export const MOPCategory = MopCategory;
/** MOP direction — which values map to GREEN. */
export const MopDirection = {
    HIGHER_IS_BETTER: 'HIGHER_IS_BETTER',
    LOWER_IS_BETTER: 'LOWER_IS_BETTER',
};
/** Value source for observed MOP data */
export const MopValueSource = {
    COMPUTED: 'COMPUTED',
    MANUAL_ENTRY: 'MANUAL_ENTRY',
};
/**
 * Origin of a MoP threshold/objective requirement, per MEIA schema v4.3.5
 * `mission.measuresOfPerformance[].requirementProvenance.sourceType`.
 *
 * Values map to canonical schema labels:
 *   CONOPS          → "CONOPS"
 *   CONTRACTOR_SPEC → "ContractorSpec"
 *   DT_AND_E        → "DT&E"   (Developmental Test & Evaluation)
 *   INTELLIGENCE    → "Intelligence"
 *   JCIDS           → "JCIDS"  (Joint Capabilities Integration & Development System)
 *   M_AND_S         → "M&S"    (Modeling & Simulation)
 *   OT_AND_E        → "OT&E"   (Operational Test & Evaluation)
 *   SME             → "SME"    (Subject Matter Expert)
 */
export const MopRequirementProvenanceSourceType = {
    CONOPS: 'CONOPS',
    CONTRACTOR_SPEC: 'CONTRACTOR_SPEC',
    DT_AND_E: 'DT_AND_E',
    INTELLIGENCE: 'INTELLIGENCE',
    JCIDS: 'JCIDS',
    M_AND_S: 'M_AND_S',
    OT_AND_E: 'OT_AND_E',
    SME: 'SME',
};
/**
 * Confidence in the MoP requirement provenance, per MEIA schema v4.3.5
 * `mission.measuresOfPerformance[].requirementProvenance.confidenceLevel`.
 */
export const MopRequirementProvenanceConfidence = {
    HIGH: 'HIGH',
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
};
/**
 * Override validation — enforces HITL audit fields when applied=true.
 */
export function validateOverride(override) {
    if (!override.applied)
        return null;
    const missing = [];
    if (override.overrideBand === null)
        missing.push('overrideBand');
    if (override.rationale === null || override.rationale === '')
        missing.push('rationale');
    if (override.authorizedBy === null || override.authorizedBy === '')
        missing.push('authorizedBy');
    if (override.timestamp === null || override.timestamp === '')
        missing.push('timestamp');
    if (missing.length === 0)
        return null;
    return {
        code: 'OVERRIDE_AUDIT_VIOLATION',
        entityId: 'unknown',
        entityType: 'override',
        message: `Override applied=true but missing required fields: ${missing.join(', ')}. HITL audit trail incomplete.`,
        severity: 'ERROR',
    };
}
/**
 * Runtime shape guard for effect-name values.
 *
 * This intentionally does not encode the Effect taxonomy. Entity-backed
 * membership validation is handled by the API mapper before the pure
 * engine receives raw MEIA snapshots.
 */
export function isDoctrinalEffectType(value) {
    return value.trim().length > 0;
}
